// ─── UNIFIED RELATIONSHIP DYNAMICS ──────────────────────────────────────────
//
// ONE relationship feature. Haley, 2026-08-02: "let's make this a way better
// feature that actually connects to our stories and directly to how the ai key
// interacts with us", "it needs to be uniform everywhere", "we also need it to
// be different for every chat".
//
// WHAT THIS REPLACES
// ------------------
// Three mechanisms existed for "what kind of relationship is this", none of
// which could see the others:
//
//   1. character_dynamics.relationship_stage — FREE TEXT, global per pair.
//      Rich six-field detail, but no typed value and no per-chat scope.
//   2. dm_conversations.story_preferences.relationshipStage — an enum, but DM
//      only, and it described the STAGE, not the kind.
//   3. SixGuysRP charTags (love/lover/friend/enemy) — per story, persisted in a
//      JSON blob, invisible to every other surface.
//
// Because no machine-readable relationship type existed anywhere, the prompt
// builders could not branch on one — so romance was simply hardcoded on every
// path. That is the actual root cause behind "every text convo ends up being
// romantic... some characters truly hate each other".
//
// THE MULTIPLE-LOVE-INTEREST PROBLEM
// ----------------------------------
// Haley: "there are routes where a character has more than one love interest so
// that needs to be taken into consideration as well."
//
// A single `romantic` value silently means EXCLUSIVE, and the old exclusivity
// block told the model its feelings were directed ONLY at the player and that
// canon bonds should be IGNORED. In a triangle that erases the very characters
// the route is built on. Kya has Dillon, Kaleb and York — York is recorded in
// the archive as "Kaleb's rival for Kya" ("Do you have feelings for York back?",
// mystery-do-over). So romance is split three ways, and only ONE of them ever
// emits an exclusivity claim.

import { supabase } from './supabaseClient.js';
import { CHARACTERS } from './characters.js';

// ─── THE KINDS ─────────────────────────────────────────────────────────────
// `romantic` here means "counts as a romance for pacing/content purposes", NOT
// "exclusive". Only romantic_exclusive is exclusive. That distinction is the
// whole point of the split.
export const RELATIONSHIP_KINDS = [
  { value: 'stranger',           label: 'Stranger',          emoji: '❔', romantic: false, blurb: 'No history. A first meeting.' },
  { value: 'acquaintance',       label: 'Acquaintance',      emoji: '🤝', romantic: false, blurb: 'Aware of each other, surface level.' },
  { value: 'friend',             label: 'Friend',            emoji: '🙂', romantic: false, blurb: 'Genuine warmth, no attraction.' },
  { value: 'close_friend',       label: 'Close Friend',      emoji: '💛', romantic: false, blurb: 'Deep trust, shares personal things.' },
  { value: 'family',             label: 'Family',            emoji: '🩸', romantic: false, blurb: 'Blood or chosen. Permanent.' },
  { value: 'mentor',             label: 'Mentor / Student',  emoji: '📖', romantic: false, blurb: 'One teaches, one learns.' },
  { value: 'rival',              label: 'Rival',             emoji: '⚔️', romantic: false, blurb: 'Competitive friction, maybe grudging respect.' },
  { value: 'enemy',              label: 'Enemy',             emoji: '🗡️', romantic: false, blurb: 'Real hostility. Does not soften on its own.' },
  { value: 'complicated',        label: "It's Complicated",  emoji: '🌀', romantic: false, blurb: 'Mixed signals, unresolved, unclear status.' },
  { value: 'romantic_exclusive', label: 'Romantic — Exclusive', emoji: '❤️', romantic: true, exclusive: true, blurb: 'One partner. Canon bonds set aside for this route.' },
  { value: 'romantic_contested', label: 'Romantic — One of Several', emoji: '💞', romantic: true, blurb: 'A real love interest ALONGSIDE others. Nothing is settled.' },
  { value: 'romantic_past',      label: 'Romantic — In the Past', emoji: '🥀', romantic: true, blurb: 'Over, but it still colours everything.' },
];

export const KIND_BY_VALUE = Object.fromEntries(RELATIONSHIP_KINDS.map(k => [k.value, k]));

export function isRomanticKind(kind) { return !!KIND_BY_VALUE[kind]?.romantic; }
export function isExclusiveKind(kind) { return !!KIND_BY_VALUE[kind]?.exclusive; }

const SELECT = 'id, character_id, target_character_id, target_identity_type, target_name, ' +
  'behavioral_summary, emotional_patterns, texting_style, vocabulary_notes, ' +
  'relationship_stage, never_do, relationship_kind, scope_type, scope_id, ' +
  'contested_with, authored, updated_at';

/** Normalise a scope. A null/absent scope means the pair's global default. */
export function makeScope(scopeType, scopeId) {
  if (!scopeType || !scopeId) return { scope_type: null, scope_id: null };
  return { scope_type: String(scopeType), scope_id: String(scopeId) };
}

// ─── READ ──────────────────────────────────────────────────────────────────

/**
 * Every relationship row for one target, across the whole cast, for one scope.
 *
 * Returns a map characterId -> resolved row, applying the resolution order
 * SCOPED ROW -> GLOBAL ROW -> nothing. One query serves a whole cast grid,
 * which is what makes the bulk editor cheap.
 */
export async function loadCastRelationships(targetCharacterId, { scopeType = null, scopeId = null, userId = null } = {}) {
  if (!targetCharacterId) return {};
  try {
    let q = supabase.from('character_dynamics').select(SELECT)
      .eq('target_character_id', targetCharacterId);
    // Fetch the global rows AND this scope's rows in one round trip, then let
    // the scoped one win below.
    if (scopeType && scopeId) {
      q = q.or(`and(scope_type.is.null,scope_id.is.null),and(scope_type.eq.${scopeType},scope_id.eq.${scopeId})`);
    } else {
      q = q.is('scope_type', null);
    }
    if (userId) q = q.or(`user_id.is.null,user_id.eq.${userId}`);

    const { data, error } = await q;
    if (error) {
      console.warn('[RelationshipDynamics] load failed:', error.message);
      return {};
    }
    const out = {};
    for (const row of (data || [])) {
      const prev = out[row.character_id];
      // A scoped row always beats a global one.
      if (!prev || (row.scope_type && !prev.scope_type)) out[row.character_id] = row;
    }
    return out;
  } catch (err) {
    console.warn('[RelationshipDynamics] load error:', err);
    return {};
  }
}

/** One pair, resolved. Scoped row -> global row -> null. */
export async function resolveRelationship(characterId, targetCharacterId, opts = {}) {
  if (!characterId || !targetCharacterId) return null;
  const all = await loadCastRelationships(targetCharacterId, opts);
  return all[characterId] || null;
}

// ─── WRITE ─────────────────────────────────────────────────────────────────

/**
 * Bulk save. Haley: "we can adjust it at anytime and multiple characters at
 * once". Idempotent by (character, target, identity type, user, scope) — the
 * unique index added in 20260802010000 backs the upsert, so saving the same
 * grid twice updates rather than duplicating.
 *
 * Rows written here are marked `authored` so a later generation pass cannot
 * silently overwrite a human's choice.
 */
export async function saveRelationships(rows, { scopeType = null, scopeId = null, userId = null } = {}) {
  const list = (rows || []).filter(r => r?.character_id && r?.target_character_id);
  if (!list.length) return { saved: 0, error: null };

  const scope = makeScope(scopeType, scopeId);
  const payload = list.map(r => ({
    character_id: r.character_id,
    target_character_id: r.target_character_id,
    target_identity_type: r.target_identity_type || 'existing_character',
    target_name: r.target_name || null,
    // behavioral_summary is NOT NULL in the schema; a kind-only row is a
    // perfectly reasonable thing to save from the grid, so give it a body
    // rather than rejecting the write.
    behavioral_summary: (r.behavioral_summary && r.behavioral_summary.trim())
      || `${KIND_BY_VALUE[r.relationship_kind]?.label || 'Relationship'} — set from the relationships editor.`,
    emotional_patterns: r.emotional_patterns || null,
    texting_style: r.texting_style || null,
    vocabulary_notes: r.vocabulary_notes || null,
    relationship_stage: r.relationship_stage || null,
    never_do: r.never_do || null,
    relationship_kind: r.relationship_kind || null,
    contested_with: Array.isArray(r.contested_with) ? r.contested_with : [],
    authored: true,
    user_id: userId || null,
    ...scope,
  }));

  const { error } = await supabase
    .from('character_dynamics')
    .upsert(payload, {
      onConflict: 'character_id,target_character_id,target_identity_type,user_id,scope_type,scope_id',
    });
  if (error) {
    console.warn('[RelationshipDynamics] save failed:', error.message);
    return { saved: 0, error: error.message };
  }
  return { saved: payload.length, error: null };
}

/** Remove a per-chat override so the pair falls back to its global default. */
export async function clearScopedRelationship(characterId, targetCharacterId, { scopeType, scopeId }) {
  if (!scopeType || !scopeId) return { error: 'refusing to clear the global row from here' };
  const { error } = await supabase.from('character_dynamics').delete()
    .eq('character_id', characterId)
    .eq('target_character_id', targetCharacterId)
    .eq('scope_type', scopeType)
    .eq('scope_id', scopeId);
  return { error: error?.message || null };
}

// ─── PROMPT ────────────────────────────────────────────────────────────────

const nameOf = (id) => CHARACTERS.find(c => c.id === id)?.name || id;

/**
 * THE RELATIONSHIP FRAME — the single place that decides whether a scene is
 * romantic, and if so on what terms.
 *
 * Every surface calls this instead of hardcoding a romance block. Absent a
 * relationship kind it returns the neutral frame, which is the 2026-08-02
 * default: romance is NOT the gravitational pull of a conversation.
 */
export function relationshipFrameFor(kind, { playerLabel = 'them', contestedWith = [] } = {}) {
  const who = playerLabel || 'them';
  switch (kind) {
    case 'romantic_exclusive':
      return `RELATIONSHIP FRAME — ROMANTIC, EXCLUSIVE:\n` +
        `• ${who} is your romantic focus in this story. Your feelings and affection are directed at them.\n` +
        `• Do NOT reference romantic history with other characters from your backstory while talking to them, and never act as if you are "taken" by someone else.`;

    case 'romantic_contested': {
      // The rivals are NAMED. An unnamed rival is one the model will quietly
      // forget exists, which collapses a triangle back into exclusivity.
      const rivals = (contestedWith || []).map(nameOf).filter(Boolean);
      const rivalLine = rivals.length
        ? `• The others are REAL and still in the picture: ${rivals.join(', ')}. Do not pretend they are absent, and do not resolve this on your own — the tension between them is the story.\n`
        : `• You have more than one love interest in this route. Do not pretend the others are absent, and do not resolve it on your own.\n`;
      return `RELATIONSHIP FRAME — ROMANTIC, ONE OF SEVERAL:\n` +
        `• You genuinely want ${who}. That is real, and you may show it.\n` +
        rivalLine +
        `• You are NOT exclusive and must never claim to be. No promises of "only you", no acting as though the choice is already made. Being torn is in character; pretending you are settled is not.`;
    }

    case 'romantic_past':
      return `RELATIONSHIP FRAME — ROMANTIC, IN THE PAST:\n` +
        `• Whatever you and ${who} were, it is over. It still colours how you speak to them — what you avoid, what lands too hard, what you cannot say lightly.\n` +
        `• Do not play this as a live romance and do not rekindle it on your own initiative. The history is a weight, not a current.`;

    case 'enemy':
      return `RELATIONSHIP FRAME — ENEMY:\n` +
        `• You do not like ${who} and you are not looking to be talked round. Hostility, contempt, suspicion and outright refusal are all correct.\n` +
        `• Do NOT drift toward warmth, banter-as-flirtation, or grudging affection over the course of the conversation. If they earn something from you it must be earned on the page, slowly, and it is not romance.`;

    case 'rival':
      return `RELATIONSHIP FRAME — RIVAL:\n` +
        `• You are in competition with ${who}. Friction, one-upmanship and a refusal to concede are correct. Respect, if any, is grudging and unspoken.\n` +
        `• Do not let rivalry slide into flirtation unless the story genuinely earns it.`;

    case 'family':
      return `RELATIONSHIP FRAME — FAMILY:\n` +
        `• ${who} is family. That bond is permanent and it is NOT romantic — never play it as attraction.\n` +
        `• Family also means old grievances, obligation, and the particular bluntness people only use with relatives.`;

    case 'mentor':
      return `RELATIONSHIP FRAME — MENTOR/STUDENT:\n` +
        `• Your relationship with ${who} is built on teaching and learning. Play the authority gap honestly. It is not romantic.`;

    case 'friend':
    case 'close_friend':
      return `RELATIONSHIP FRAME — ${kind === 'close_friend' ? 'CLOSE FRIEND' : 'FRIEND'}:\n` +
        `• You genuinely care about ${who} and it is NOT romantic. Warmth without attraction — do not let affection drift into flirtation.\n` +
        `• ${kind === 'close_friend' ? 'You trust them with things you tell almost nobody.' : 'You are comfortable together without being intimate.'}`;

    case 'stranger':
      return `RELATIONSHIP FRAME — STRANGER:\n` +
        `• You do not know ${who}. This is a FIRST MEETING. Do not act familiar, do not reference a shared past, and do not invent one.`;

    case 'acquaintance':
      return `RELATIONSHIP FRAME — ACQUAINTANCE:\n` +
        `• You know of ${who} but not well. Surface-level familiarity only — no intimacy you have not earned.`;

    case 'complicated':
      return `RELATIONSHIP FRAME — COMPLICATED:\n` +
        `• Whatever is between you and ${who} is genuinely unresolved. Mixed signals are correct. Do NOT resolve it into a clean romance or a clean rejection — hold the ambiguity.`;

    default:
      // The 2026-08-02 default. No kind set means no assumption of romance.
      return `RELATIONSHIP FRAME:\n` +
        `• Romance is NOT the default and must never be the gravitational pull of this conversation. No relationship type has been set for you and ${who}.\n` +
        `• Do not flirt, charm, or angle toward attraction unless your canon relationship already is romantic, or this conversation has genuinely earned it.\n` +
        `• Many of these characters are rivals, enemies, family, colleagues, wary strangers, or people who genuinely dislike each other — play that honestly. Dislike, indifference, suspicion, irritation and outright hostility are all valid and should stay that way for as long as your character would hold them. Warmth is earned over time, not extended by default.`;
  }
}

/**
 * The full relationship section: the typed frame first (it decides behaviour),
 * then the authored detail. Drop-in replacement for formatDynamicsForPrompt.
 */
export function formatRelationshipForPrompt(rel, { playerLabel = '' } = {}) {
  const frame = relationshipFrameFor(rel?.relationship_kind, {
    playerLabel,
    contestedWith: rel?.contested_with || [],
  });
  if (!rel) return frame;

  const target = (rel.target_name || playerLabel || 'this person').toUpperCase();
  const parts = [frame, `\nRELATIONSHIP DYNAMICS WITH ${target} — FOLLOW THESE BEHAVIORAL RULES:`];
  if (rel.behavioral_summary) parts.push(rel.behavioral_summary);
  if (rel.emotional_patterns) parts.push(`EMOTIONAL PATTERNS:\n${rel.emotional_patterns}`);
  if (rel.texting_style) parts.push(`TEXTING STYLE:\n${rel.texting_style}`);
  if (rel.vocabulary_notes) parts.push(`VOCABULARY & LANGUAGE:\n${rel.vocabulary_notes}`);
  if (rel.relationship_stage) parts.push(`CURRENT RELATIONSHIP STAGE:\n${rel.relationship_stage}`);
  if (rel.never_do) parts.push(`NEVER DO (HARD RULES — violating these is COMPLETELY out of character):\n${rel.never_do}`);
  return parts.join('\n');
}

/**
 * Seed rows for a whole cast. Haley: "relationship dynamics with all characters
 * in a stories' cast". Nothing is written — this returns editable rows so the
 * grid can show every cast member immediately, with whatever is already saved
 * merged in.
 */
export function buildCastRows(castIds, targetCharacterId, existing = {}) {
  const targetName = nameOf(targetCharacterId);
  return (castIds || [])
    .filter(id => id && id !== targetCharacterId)
    .map(id => {
      const row = existing[id];
      return row
        ? { ...row, name: nameOf(id), scoped: !!row.scope_type }
        : {
            character_id: id,
            target_character_id: targetCharacterId,
            target_identity_type: 'existing_character',
            target_name: targetName,
            name: nameOf(id),
            relationship_kind: null,
            behavioral_summary: '', emotional_patterns: '', texting_style: '',
            vocabulary_notes: '', relationship_stage: '', never_do: '',
            contested_with: [], authored: false, scoped: false,
          };
    });
}
